import { useEffect, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { cn } from "./cn";
import { MARK } from "./Mark";

const TICK = 380;
const PHASES = 5;
const draw = { type: "spring", stiffness: 520, damping: 22 } as const;

/**
 * The mark building itself on a loop: the two bars snap up one by one, the finished mark skews on a spring, then the
 * bars drop out and it starts over.
 */
export function MarkSpinner({ size = 16, className }: { size?: number; className?: string }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((n) => n + 1), TICK);
    return () => clearInterval(t);
  }, []);
  const phase = step % PHASES;
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      aria-hidden
      className={cn("shrink-0 overflow-visible", className)}
      animate={{ skewY: phase === 3 ? -12 : 0 }}
      transition={{ type: "spring", stiffness: 380, damping: 14 }}
    >
      {MARK.parts.map((p, i) => (
        <motion.path
          key={p.d}
          d={p.d}
          fill={p.fill}
          style={{ transformBox: "fill-box", transformOrigin: "bottom" }}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: phase >= i && phase < 4 ? 1 : 0 }}
          transition={draw}
        />
      ))}
    </motion.svg>
  );
}

export type MarkLoaderProps = {
  /** What is loading, said in a few words. Also read out to screen readers. */
  label?: ReactNode;
  /** `stack` centres a large mark over the label for a page or a sheet; `row` sits a small one beside it for a section. */
  layout?: "stack" | "row";
  /** Seconds to stay hidden first, so quick loads never flash it. */
  delay?: number;
  className?: string;
};

/** The house loading state: the building mark and what it is waiting on. Buttons keep the plain `Spinner`. */
export function MarkLoader({ label, layout = "stack", delay = 0, className }: MarkLoaderProps) {
  const row = layout === "row";
  return (
    <motion.div
      role="status"
      aria-live="polite"
      className={cn(row ? "flex items-center gap-3" : "flex flex-col items-center gap-4 py-16 text-center", className)}
      initial={{ opacity: 0, y: row ? 0 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30, delay }}
    >
      <MarkSpinner size={row ? 20 : 44} />
      {label ? <span className={cn("text-[15px]", row ? "text-ink" : "font-semibold text-ink-soft")}>{label}</span> : <span className="sr-only">Loading</span>}
    </motion.div>
  );
}
